import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { TextInput } from '@common/TextInput/TextInput';
import { useRouter } from 'next/router';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { VariantAxis, VariantAxisItem } from '@hooks/states/variantSelectionSlice';
import { TextInputLabel } from '@common/TextInput/TextInputLabel';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { ProductAttributeValueAmountUnit, ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAmountLabel } from '@libs/attribute.helper';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { InputChangeConfirmationWrapper } from '@common/InputChangeConfirmationWrapper/InputChangeConfirmationWrapper';
import { getDeliveryTimeIconLayout } from '@common/DeliveryInfo/DeliveryInfo';
import type { IconColor, IconID } from '@common/Icon/Icon';
import { Icon } from '@common/Icon/Icon';
import type { DropdownIndicatorProps } from 'react-select';
import Select, { components } from 'react-select';
import classNames from 'classnames';
import Image from 'next/image';
import { CopyToClipboard } from '@product/CopyToClipboard/CopyToClipboard';
import styles from './VariantSelector.module.scss';
import type { UnitSystem } from '@icommerce/units';
import { getUnitLabelForUnitSystem } from '@icommerce/units';
import { getProductIdForLocale } from '@libs/product.helper';
import { APP_NAME, DeliveryTime, LOCALES_TO_USE_US_PRODUCT_ID } from '@libs/constants';

export interface VariantSelectorProps {
  columnLayout?: boolean;
}

interface VariantAxisOption {
  value: string | ProductAttributeValueAmountUnit;
  label: string;
}

const DropdownIndicator = (props: DropdownIndicatorProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.DropdownIndicator {...props}>
    <Image width={10} height={10} src={`/_${APP_NAME}/icons/igus-icon-arrow-down.svg`} alt="dropdown" />
  </components.DropdownIndicator>
);

export const getSelectedItemForVariantAxis = (
  variantAxis: VariantAxis,
  currentProduct: ProductFragmentEnhanced
): VariantAxisItem | undefined =>
  variantAxis.items.find(item => {
    const productValue = getAttributeValue<ProductAttributeValueAmountUnit>(
      currentProduct.attributes?.[variantAxis.attribute] as ProductAttribute<ProductAttributeValueAmountUnit>
    );
    if (typeof item === 'object' && item.amount !== undefined && productValue?.amount !== undefined) {
      return item.amount === productValue.amount;
    }
    return item === productValue;
  });

export const getVariantAxisItemValueMap = (variantAxis: VariantAxis, unitSystem: UnitSystem): Map<string, string> =>
  // the dropdown component requires a map with <label, value> where the value is  sku ore multiple skus (needed for the onChange handler)
  variantAxis.items.reduce((map: Map<string, string>, item: VariantAxisItem) => {
    if (typeof item === 'string' || typeof item === 'number') {
      map.set(item.toString(), item.toString());
      return map;
    }

    if (item?.amount === undefined) {
      return map;
    }

    map.set(item.amount.toString(), getAttributeValueAmountLabel(item, unitSystem));
    return map;
  }, new Map<string, string>());

export const getVariantAxisItemValueString = (item: VariantAxisItem | undefined): string => {
  if (!item) {
    return '';
  }
  if (typeof item === 'object') {
    return item.amount.toString();
  }
  if (typeof item === 'number') {
    return item.toString();
  }
  return item.toString() || '';
};

const formatToDecimal = (value: string, locale = 'de-DE') =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
  }).format(parseFloat(value));

export const getReadonlyVariantAxisItemValueString = (
  variantAxis: VariantAxis,
  item: VariantAxisItem,
  locale: string | undefined,
  unitSystem: UnitSystem
): string => {
  if (typeof item === 'string' || typeof item === 'number') {
    return item.toString();
  }

  return getAttributeValueAmountLabel(item, unitSystem, {
    amountFormatter: a =>
      variantAxis.attribute.includes('outer_diameter') ? formatToDecimal(a.toString(), locale) : a.toString(),
  });
};

const getVariantAxisLabel = (variantAxis: VariantAxis, unitSystem: UnitSystem): string => {
  const defaultUnitCode = (variantAxis.items?.[0] as ProductAttributeValueAmountUnit)?.unit;
  const unitDisplayLabel = getUnitLabelForUnitSystem(defaultUnitCode, unitSystem);

  const unitLabel = unitDisplayLabel ? ` [${unitDisplayLabel}]` : '';
  return `${variantAxis.label}${unitLabel}`;
};

export const VariantSelector = ({ columnLayout }: VariantSelectorProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation(['product/productDetails', 'product/echainBuyBox']);

  const allProducts = useProductPageStore(state => state.allProducts);
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const availableVariantAxes = useProductPageStore(state => state.availableVariantAxes);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const iaConfig = useEchainProductStore(store => store.eChainPartsList?.iaConfig);
  const removeIaConfig = useEchainProductStore(store => store.removeIaConfig);

  const setSelectedVariantSku = useProductPageStore(state => state.setSelectedVariantSku);
  const setVariantAxisValue = useProductPageStore(state => state.setVariantAxisValue);

  const partnumberImperialOld: string = getAttributeValue(currentProduct.attributes.partnumber_imperial_old);

  const showOldImperialPartnumber = useMemo(
    () =>
      LOCALES_TO_USE_US_PRODUCT_ID.includes(locale) &&
      partnumberImperialOld &&
      partnumberImperialOld !== getAttributeValue(currentProduct.attributes.partnumber_imperial),
    [currentProduct, locale]
  );

  const variantInputs = useMemo(
    () =>
      availableVariantAxes
        ? availableVariantAxes.map(variantAxis => {
            const selectedItem = getSelectedItemForVariantAxis(variantAxis, currentProduct);
            const variantAxisLabel = getVariantAxisLabel(variantAxis, unitSystem);
            // read only variant axes are displayed as a disabled input field
            if (variantAxis.readonly && selectedItem) {
              const id = `variant-textinput-${variantAxis.attribute}`;
              const value = getReadonlyVariantAxisItemValueString(variantAxis, selectedItem, locale, unitSystem);
              return (
                <div className="mb-8" key={id}>
                  <TextInputLabel label={variantAxisLabel}>
                    <TextInput value={value} disabled styles="my-4" />
                  </TextInputLabel>
                </div>
              );
            }

            if (variantAxis.items.length) {
              const values = getVariantAxisItemValueMap(variantAxis, unitSystem);
              const valueList: VariantAxisOption[] = [];
              values.forEach((value: string, key: string) => {
                valueList.push({ value: key, label: value });
              });
              const id = `variant-dropdown-${variantAxis.attribute}`;
              const selectedItemValue = getVariantAxisItemValueString(selectedItem);

              return (
                <div key={id}>
                  <div className="flex flex-row mb-4">
                    <label data-testid={`${id}-label`} htmlFor={id} className="block">
                      {variantAxisLabel}
                    </label>
                  </div>
                  <InputChangeConfirmationWrapper
                    key={id}
                    text={t('ECHAIN_PRODUCT_PAGE.USER_CONFIRMATION_MODAL.DESCRIPTION', { ns: 'product/echainBuyBox' })}
                    hasToConfirm={Boolean(iaConfig?.configId)}
                    actionToConfirm={removeIaConfig}
                  >
                    <Select
                      instanceId={`variant_axis_select_${variantAxis.attribute}`}
                      placeholder={t('PRODUCT_DETAILS.PRODUCT_NO')}
                      value={valueList.filter(item => item.value === selectedItemValue)}
                      options={valueList}
                      onChange={(value: { value?: string }) =>
                        setVariantAxisValue(value?.value || '', variantAxis.attribute)
                      }
                      isClearable={false}
                      test-attribute_outer_diameter
                      isSearchable={false}
                      name={variantAxis.attribute}
                      inputId={id}
                      closeMenuOnSelect
                      components={{ DropdownIndicator, IndicatorSeparator: undefined }}
                      classNames={{
                        control: state =>
                          state.isFocused ? styles.variantDropdownControlDarkBorder : styles.variantDropdownControl,
                        option: state =>
                          state.isFocused
                            ? styles.variantDropdownControlOptionFocus
                            : styles.variantDropdownControlOption,
                        singleValue: () => styles.variantDropdownSingleValue,
                        menu: () => styles.variantDropdownMenu,
                      }}
                    />
                  </InputChangeConfirmationWrapper>
                </div>
              );
            }
            return null;
          })
        : [],
    [availableVariantAxes, currentProduct, locale, iaConfig, setVariantAxisValue, removeIaConfig, t, unitSystem]
  );

  type SkuDropdownItem = {
    text: string;
    value: string;
    iconColor: IconColor;
    iconID: IconID;
  };

  const skuDropdownItems: SkuDropdownItem[] = useMemo(
    () =>
      allProducts.map(product => {
        const deliveryTimeCode =
          product.delivery_times && product.delivery_times.length
            ? (product.delivery_times[0].code?.toString() as DeliveryTime)
            : DeliveryTime.ON_REQUEST;
        const { iconColor, iconID } = getDeliveryTimeIconLayout(deliveryTimeCode);

        return {
          text: getProductIdForLocale(product, locale),
          value: product.id,
          iconColor,
          iconID,
        };
      }),
    [allProducts, locale]
  );

  return (
    <div
      className={
        columnLayout ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 igus-text-styles' : 'flex flex-col gap-8'
      }
      data-testid="variantSelector"
    >
      <div>
        <div className="flex flex-row mb-4">
          <label htmlFor="variant-dropdown-sku" className="block">
            {t('PRODUCT_DETAILS.PRODUCT_NO')}
          </label>
          <CopyToClipboard
            text={getProductIdForLocale(currentProduct, locale)}
            copiedLabel={t('PRODUCT_DETAILS.PRODUCT_NO')}
          />
        </div>
        <div data-testid="sku-dropdown-wrapper">
          <InputChangeConfirmationWrapper
            text={t('ECHAIN_PRODUCT_PAGE.USER_CONFIRMATION_MODAL.DESCRIPTION', { ns: 'product/echainBuyBox' })}
            hasToConfirm={Boolean(iaConfig?.configId)}
            actionToConfirm={removeIaConfig}
          >
            <Select
              instanceId="sku_select"
              placeholder={t('PRODUCT_DETAILS.PRODUCT_NO')}
              value={skuDropdownItems.filter(item => item.value === currentProduct.id)}
              options={skuDropdownItems}
              onChange={(value: { value?: string }) => {
                setSelectedVariantSku(value?.value || '');
              }}
              // eslint-disable-next-line react/no-unstable-nested-components
              formatOptionLabel={(item: SkuDropdownItem) => (
                <div className={classNames(styles.variantDropdownOptionContainer)}>
                  <span
                    data-testid="product-no-dropdown-option-icon"
                    className={classNames('mr-2', styles.variantDropdownOptionDeliveryTimeIcon)}
                  >
                    <Icon id={item.iconID} size={8} color={item.iconColor} />
                  </span>
                  <span data-testid="product-no-dropdown-option-text" className={classNames('pt-1')}>
                    {item.text}
                  </span>
                </div>
              )}
              isClearable={false}
              isSearchable={false}
              isDisabled={!skuDropdownItems || skuDropdownItems.length < 2}
              name="variant-dropdown-sku"
              inputId="variant-dropdown-sku"
              closeMenuOnSelect
              components={{ DropdownIndicator, IndicatorSeparator: undefined }}
              classNames={{
                control: state => {
                  if (state.isDisabled) {
                    return styles.variantDropdownControlDisabled;
                  }
                  if (state.isFocused) {
                    return styles.variantDropdownControlDarkBorder;
                  }
                  return styles.variantDropdownControl;
                },
                option: state =>
                  state.isFocused ? styles.variantDropdownControlOptionFocus : styles.variantDropdownControlOption,
                singleValue: state =>
                  state.isDisabled ? styles.variantDropdownSingleValueDisabled : styles.variantDropdownSingleValue,
                menu: () => styles.variantDropdownMenu,
                indicatorsContainer: state => (state.isDisabled ? '!hidden' : ''),
              }}
            />
          </InputChangeConfirmationWrapper>
        </div>
        {showOldImperialPartnumber && (
          <span data-testid="partnumber-imperial-old" className="text-xs font-light">
            {t('PRODUCT_DETAILS.FORMERLY_KNOWN_AS')}: {partnumberImperialOld}
          </span>
        )}
      </div>
      {allProducts && allProducts.length && variantInputs}
    </div>
  );
};
